import React from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";

import { SUBMITTER_IS_CONTRIBUTOR_CHANGED } from "intro/introActions";

import { getStepFields, isIntroStepLoading } from "common/selectors";

import { COMPLETE_STEP } from "intro/introActions";

import * as steps from "common/steps";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import Button from "@material-ui/core/Button";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import LoadingPanel from "general-components/LoadingPanel";
import Typography from "@material-ui/core/Typography";
import PersonalDetailsForm from "../../basic-details/components/PersonalDetailsForm";
import FormLabel from "@material-ui/core/FormLabel";
import { useTranslation } from "react-i18next";
import { FormControl } from "@material-ui/core";
import { getFieldOptions } from "../../common/selectors";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },

  form: {
    height: "100%",
    maxHeight: 800,
    minHeight: 400,
  },

  formBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    maxHeight: 800,
    minHeight: 400,
  },

  introSubmitterQuestion: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },

  introQuestion: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },

  radioGroup: {
    minWidth: 125,
    display: "flex",
    flexDirection: "row",
  },

  radioBox: {
    maxWidth: 150,
    minWidth: 140,
    maxHeight: 75,
    borderStyle: "solid",
    borderColor: theme.palette.primary.light,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },

  selectedRadioBox: {
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
  },

  stepperNavigation: {
    flexGrowth: 1,
    display: "flex",
    justifyContent: "flex-end",
  },

  stepperNavigationRtl: {
    justifyContent: "flex-start",
  },

  nextButton: {
    fontSize: "1rem",
    fontWeight: "bold",
  },

  endIconRtl: {
    order: -1,
  },
}));

const Intro = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const fields = useSelector(getStepFields(steps.INTRO));
  const loading = useSelector(isIntroStepLoading());
  const fieldOptions = useSelector(getFieldOptions);

  const showNoOption = !fieldOptions?.is_submitter_creator__no?.hidden ?? true;

  const dispatch = useDispatch();
  const onNext = () => dispatch({ type: COMPLETE_STEP });

  const handleRadioChange = event => {
    const val = event.target.value;
    if (val === "yes") {
      dispatch({ type: SUBMITTER_IS_CONTRIBUTOR_CHANGED, value: true });
    } else {
      dispatch({ type: SUBMITTER_IS_CONTRIBUTOR_CHANGED, value: false });
    }
  };

  return loading ? (
    <LoadingPanel />
  ) : (
    <Paper classes={{ root: classes.root }} elevation={0}>
      <div className={classes.formBody}>
        <div>
          <div>
            <Typography variant="h2">
              {t("intro_greeting")}
              <Typography variant="body2">{t("intro_greeting_sub")}</Typography>
            </Typography>
            <Typography variant="subtitle1">
              {t("intro_greeting_extra")}
            </Typography>
          </div>

          <FormControl component="fieldset">
            <div className={classes.introQuestion}>
              <FormLabel
                id="creator-legend"
                component="legend"
                color="secondary"
              >
                {t("intro_question")}
              </FormLabel>
              <Typography variant="subtitle1">
                {t("intro_question_extra")}
              </Typography>
            </div>

            <RadioGroup
              classes={{ root: classes.radioGroup }}
              id="creator"
              aria-labelledby="creator-legend"
              name="creator"
              value={fields.isSubmitterCreator ? "yes" : "no"}
              onChange={handleRadioChange}
            >
              <div
                className={clsx(
                  classes.radioBox,
                  fields.isSubmitterCreator && classes.selectedRadioBox,
                )}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={t("intro_is_submitter_yes")}
                />
              </div>

              {showNoOption && (
                <div
                  className={clsx(
                    classes.radioBox,
                    !fields.isSubmitterCreator && classes.selectedRadioBox,
                  )}
                >
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label={t("intro_is_submitter_no")}
                  />
                </div>
              )}
            </RadioGroup>
          </FormControl>

          {!fields.isSubmitterCreator && (
            <div>
              <div className={classes.introSubmitterQuestion}>
                <Typography variant="h2">
                  {t("intro_submitter_creator")}
                  <Typography variant="body2">
                    {t("intro_submitter_creator_sub")}
                  </Typography>
                </Typography>
              </div>
              <PersonalDetailsForm />
            </div>
          )}
        </div>

        <div
          className={clsx(classes.stepperNavigation, {
            [classes.stepperNavigationRtl]: i18n.dir() === "rtl",
          })}
        >
          <Button
            classes={{
              root: classes.nextButton,
              endIcon: clsx({
                [classes.endIconRtl]: i18n.dir() === "rtl",
              }),
            }}
            variant="text"
            onClick={onNext}
            endIcon={<ArrowForwardIcon />}
          >
            {t("intro_get_started")}
          </Button>
        </div>
      </div>
    </Paper>
  );
};

export default Intro;
